import axios from 'axios'
import { messageForCode, synthetic } from '@/lib/error-messages'
import { api } from './client'

export type UserRole = 'seller' | 'coach' | 'admin'

/** In-app user shape. Backend emits snake_case fields; we normalize at the
 * API boundary so components stay in camelCase. */
export interface AuthenticatedUser {
  id: string
  role: UserRole
  fullName: string
}

interface UserPayload {
  id: string
  role: UserRole
  // Бэкенд-JSON шлёт snake_case (plan §5.1); маппится в camelCase в
  // toAuthenticatedUser ниже.
  // biome-ignore lint/style/useNamingConvention: mirrors backend JSON schema
  full_name: string
}

export interface LoginRequest {
  login: string
  password: string
}

interface LoginResponse {
  user: UserPayload
}

export interface UserStore {
  id: string
  name: string
  city: string
}

interface MePayload extends UserPayload {
  stores: UserStore[]
}

/**
 * MeResponse — результат GET /me: профиль плюс привязанные магазины.
 * user-поля нормализуются из snake_case, stores передаются как есть —
 * они уже в camelCase/одноуровневые на бэкенде.
 */
export interface MeResponse extends AuthenticatedUser {
  stores: UserStore[]
}

function toAuthenticatedUser(payload: UserPayload): AuthenticatedUser {
  return {
    id: payload.id,
    role: payload.role,
    fullName: payload.full_name,
  }
}

function toMeResponse(payload: MePayload): MeResponse {
  return {
    ...toAuthenticatedUser(payload),
    stores: payload.stores,
  }
}

export interface ApiErrorPayload {
  error: string
  message: string
}

/**
 * ApiError wraps backend {error, message} payloads so UI code can read the
 * machine-readable code without parsing axios' generic response shape every
 * time. `code` is the backend error identifier ("invalid_credentials",
 * "validation_error", ...); `message` is the human-readable text.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string

  constructor(status: number, code: string, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

/**
 * Translate an axios failure into an ApiError preserving the backend code.
 *
 * Message is always the Russian user-facing text from lib/error-messages —
 * backend English stays in server logs. Untranslated codes fall back to the
 * server-provided message, then to the caller's fallback.
 */
export function toApiError(err: unknown, fallback = 'Что-то пошло не так'): ApiError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return new ApiError(0, 'offline', synthetic('offline'))
  }
  if (axios.isAxiosError(err)) {
    if (err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout')) {
      return new ApiError(0, 'timeout', synthetic('timeout'))
    }
    if (err.response) {
      const status = err.response.status
      const data = err.response.data as Partial<ApiErrorPayload> | undefined
      const code = data?.error ?? 'unknown_error'
      return new ApiError(status, code, messageForCode(code, data?.message ?? fallback))
    }
    return new ApiError(0, 'network_error', synthetic('network_error'))
  }
  if (err instanceof Error) {
    return new ApiError(0, 'network_error', err.message || synthetic('network_error'))
  }
  return new ApiError(0, 'unknown_error', fallback)
}

/** POST /auth/login — resolves to the authenticated user on 200. */
export async function login(req: LoginRequest): Promise<AuthenticatedUser> {
  try {
    const { data } = await api.post<LoginResponse>('/auth/login', req)
    return toAuthenticatedUser(data.user)
  } catch (err) {
    throw toApiError(err, 'Не удалось войти')
  }
}

/**
 * POST /auth/logout — clears the session cookies on the backend (204).
 * Resolves on 2xx; rejects with ApiError on transport or server failure so the
 * caller can decide what to log. The UI still drops local session state in
 * either case, since the user's intent ("выйти") should not be blocked by a
 * network hiccup.
 */
export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout')
  } catch (err) {
    throw toApiError(err, 'Не удалось выйти')
  }
}

/**
 * GET /me — hydrates the current session. Returns the authenticated user
 * together with the stores they are linked to. 401 bubbles up as ApiError so
 * the caller can distinguish "not logged in" from transport failures.
 */
export async function me(): Promise<MeResponse> {
  try {
    const { data } = await api.get<MePayload>('/me')
    return toMeResponse(data)
  } catch (err) {
    throw toApiError(err, 'Не удалось получить профиль')
  }
}
